import useAppStore from "@/context/store";
import { submitNps } from "@/services/qingzhou";
import { getAdaptiveImg } from "@/utils/imageUrl";
import { useControllableValue, useSafeState } from "ahooks";
import { Button, Input, notification, Radio } from "antd";
import classNames from "classnames";
import { without } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import HBIcon from "../HBIcon";
import HBMessage from "../HBMessage";
import Image from "../Image";
import styles from "./index.module.less";

/**
 * @typedef {{
 * "id": 39,
 * "name":  string,
 * "type": 1|2,
 * "status": 1,
 * "grade_desc":  string,
 * "option_left":  string,
 * "option_right":  string,
 * "reason_desc":  string,
 * "detractors_options": string[],
 * "detractors_show": 0|1,
 * "neutral_options":  string[],
 * neutral_show: 1|0,
 * "presenter_options": string[],
 * "presenter_show": 0|1,
 * "reason_tips":  string
 * "fill_desc": string
 * "fill_tips":  string
 * "question_img_url":  string|null,
 * "presenter_desc": string
 * "neutral_desc": string
 * "detractors_desc": string
 *}} NPSData
 */

const NPS = {
  open({ data, position, onCancel }) {
    const headerHeight = useAppStore.getState().headerHeight;
    notification.open({
      key: data.id,
      className: styles.notification,
      duration: null,
      top: headerHeight + 10,
      description: <NpsDialog data={data} position={position} />,
      closeIcon: (
        <HBIcon
          data-gd-click="questionnaire_click"
          data-click-type="取消"
          data-subject-name={data.name}
          data-questionnaire-id={data.id}
          data-questionnaire-title={data.detractors_desc}
          data-questionnaire-type="NPS"
          data-post-page={position.pit_name}
          type="ic_close"
        />
      ),
      onClose: onCancel,
    });
  },
};

export default NPS;
/**
 *
 * @param {{
 * data: NPSData
 * position: {}
 * }} param0
 * @returns
 */
function NpsDialog({ data, position }) {
  // 评分不同等级
  const gradeLevel = useMemo(
    () => ({
      1: {
        presenter: 4,
        neutral: 3,
        detractors: 0,
      },
      2: {
        presenter: 9,
        neutral: 7,
        detractors: 0,
      },
    }),
    []
  );
  const [grade, setGrade] = useSafeState();
  const [optionValuse, setOptionValues] = useSafeState([]);
  const [fillContent, setFillContent] = useSafeState();
  const [errTip, setErrTip] = useSafeState("");
  const rateOptions = useMemo(() => {
    return new Array(data.type == 2 ? 11 : 6).fill(1).map((el, index) => ({
      value: index,
      label: index,
    }));
  }, [data]);

  const mutiSelectProp = useMemo(() => {
    const { presenter, neutral } = gradeLevel[data.type];
    if (grade >= presenter) {
      return {
        questionText: data.presenter_desc,
        options: data.presenter_options.map((value) => ({ value })),
        other: data.presenter_show,
      };
    }
    if (grade >= neutral) {
      return {
        questionText: data.neutral_desc,
        options: data.neutral_options.map((value) => ({ value })),
        other: data.neutral_show,
      };
    }

    return {
      questionText: data.detractors_desc,
      options: data.detractors_options.map((value) => ({ value })),
      other: data.detractors_show,
    };
  }, [grade, data, gradeLevel]);

  useEffect(() => {
    setOptionValues([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutiSelectProp]);

  useEffect(() => {
    setErrTip("");
  }, [optionValuse, setErrTip]);

  const onSubmit = useCallback(async () => {
    if (optionValuse.length === 0) {
      HBMessage.error("请选择您评分的原因哦");
      setErrTip("请选择您评分的原因哦");
      return;
    }

    if (optionValuse.includes("其他")) {
      setErrTip("请填写您的【其他】原因");
      HBMessage.error("请填写您的【其他】原因");
      return;
    }

    const res = await submitNps({
      grade,
      option_value: optionValuse,
      fill_content: fillContent,
      module_id: position?.module_id || 946,
      survey_id: data.id,
      origin_url: window.location.href,
    });

    if (res?.code) {
      HBMessage.error(res.message);
    } else {
      HBMessage.success("提交成功，感谢您的评价");
      notification.close(data.id);
    }
  }, [
    optionValuse,
    setErrTip,
    grade,
    fillContent,
    position?.module_id,
    data.id,
  ]);

  return (
    <div
      // ref={ref}
      className={styles.nps}
      data-gd-expose="questionnaire_expose"
      data-subject-name={data.name}
      data-questionnaire-id={data.id}
      data-questionnaire-title={data.detractors_desc}
      data-questionnaire-type="NPS"
      data-post-page={position.pit_name}
      // style={typeof grade === "number" ? {} : wrapStyle}
    >
      <QuestionRate
        questionText={data.grade_desc}
        img={data.question_img_url}
        options={rateOptions}
        required
        value={grade}
        leftText={data.option_left}
        rightText={data.option_right}
        onChange={(e) => setGrade(e.target.value)}
      />
      {typeof grade === "number" && (
        <>
          <QuestionMutiSelect
            {...mutiSelectProp}
            placeholder={data.reason_tips}
            required
            value={optionValuse}
            onChange={setOptionValues}
          />
          <QuestionInput
            questionText={data.fill_desc}
            value={fillContent}
            onChange={setFillContent}
            placeholder={data.fill_tips}
          />
          <div className={styles.errTip}>{errTip}</div>
          <div className={styles.npsFooter}>
            <Button
              data-gd-click="questionnaire_click"
              data-click-type="取消"
              onClick={() => notification.close(data.id)}
            >
              取消
            </Button>
            <Button
              data-gd-click="questionnaire_click"
              data-questionnaire-score={grade}
              data-options-title={optionValuse.join(",")}
              data-click-type="提交"
              onClick={onSubmit}
              type="primary"
            >
              提交
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function QuestionRate({
  options,
  questionText,
  required,
  img,
  leftText,
  rightText,
  ...props
}) {
  return (
    <div className={classNames(styles.item)}>
      <div className={styles.question}>
        {required && <span className={styles.required}> * </span>}
        <span>{questionText}</span>
      </div>
      {img && (
        <Image
          className={styles.questionImg}
          alt="questionImg"
          width={520}
          src={getAdaptiveImg(img, {
            width: 520,
          })}
        />
      )}
      <div className={styles.answer}>
        <Radio.Group
          className={styles.rate}
          options={options}
          optionType="button"
          {...props}
        />
        <div className={styles.satisfaction}>
          <span>
            <Image
              width={14}
              alt={"不满意"}
              src="https://st0.dancf.com/static/02/202209280327-9849.png"
            />
            {leftText}
          </span>
          <span>
            <Image
              width={14}
              alt={"非常满意"}
              src="https://st0.dancf.com/static/02/202209280327-0026.png"
            />
            {rightText}
          </span>
        </div>
      </div>
    </div>
  );
}

function QuestionMutiSelect({
  options,
  questionText,
  other,
  required,
  placeholder,
  img,
  value,
  onChange,
}) {
  const [selected, setSelected] = useSafeState(value || []);
  const [inputValue, setInputValue] = useSafeState();

  const wrapOption = useMemo(() => {
    if (other) {
      return [
        ...options,
        {
          value: "其他",
          hasInput: true,
        },
      ];
    }

    return options;
  }, [options, other]);

  const onSelect = useCallback(
    (item) => {
      if (selected.includes(item)) {
        setSelected(without(selected, item));
        return;
      }

      if (selected.length === 3) {
        HBMessage.error("最多可选 3 个原因");
        return;
      }

      setSelected([...selected, item]);
    },
    [selected, setSelected]
  );

  useEffect(() => {
    if (selected.includes("其他")) {
      onChange &&
        onChange([...without(selected, "其他"), inputValue || "其他"]);
    } else {
      onChange && onChange([...selected]);
    }
  }, [inputValue, onChange, selected]);

  useEffect(() => {
    if (value.length == 0 && selected.length !== 0) {
      setSelected(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSelected, value]);

  return (
    <div className={classNames(styles.item)}>
      <div className={styles.question}>
        {required && <span className={styles.required}> * </span>}
        <span>{questionText}</span>
      </div>
      {img && (
        <Image
          alt="questionImg"
          width={520}
          src={getAdaptiveImg(img, {
            width: 520,
          })}
        />
      )}
      <div className={styles.answer}>
        <div className={styles.blockOptions}>
          {wrapOption.map((r) => (
            <div
              key={r.value}
              className={classNames(styles.optionItem, {
                [styles.active]: (selected || []).includes(r.value),
              })}
              onClick={() => onSelect(r.value)}
            >
              {r.label || r.value}
            </div>
          ))}
        </div>

        {!!other && selected.includes("其他") && (
          <div className={styles.otherInput}>
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              size={"large"}
              placeholder={placeholder || "请输入其他原因"}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionInput({ questionText, required, placeholder, img, ...props }) {
  const [inputValue, setInputValue] = useControllableValue(props);

  return (
    <div className={classNames(styles.item)}>
      <div className={styles.question}>
        {required && <span className={styles.required}> * </span>}
        <span>{questionText}</span>
      </div>
      {img && (
        <Image
          alt="questionImg"
          width={520}
          src={getAdaptiveImg(img, {
            width: 520,
          })}
        />
      )}
      <div className={styles.answer}>
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          size={"large"}
          placeholder={placeholder || "请输入内容"}
        />
      </div>
    </div>
  );
}
